import { Dialog, useDialogViews } from "@resource/atlas";
import { RichBlockProviderOrganization } from "client/guide-content/rich-blocks/provider";
import { RefObject, useState } from "react";

import { PostTemplateContentDialogView } from "./PostTemplateContentDialogView";
import {
  PostTemplateSettingsDialogFormData,
  PostTemplateSettingsDialogView,
} from "./PostTemplateSettingsDialogView";
import { useDialogStateFormManagement } from "./useDialogStateFormManagement";
import { useDialogWithLeaveConfirmation } from "./useDialogWithLeaveConfirmation";
import { useContentForm, useSettingsForm } from "./useForms";
import { useUpsertPostTemplate } from "./useUpsertPostTemplate";

type CreatePostTemplateDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  organization: RichBlockProviderOrganization;
};

export function CreatePostTemplateDialog({
  organization,
  open,
  setOpen,
}: CreatePostTemplateDialogProps) {
  const [settingsData, setSettingsData] =
    useState<PostTemplateSettingsDialogFormData>();
  const [upsertPostTemplate, { loading: upsertPostTemplateLoading }] =
    useUpsertPostTemplate();

  const { useFormReturn: settingsUseFormReturn } = useSettingsForm();
  const { useFormReturn: contentUseFormReturn, showConfirmation } =
    useContentForm();

  const dialogState = useDialogWithLeaveConfirmation({
    open,
    setOpen,
    showConfirmation,
  });

  useDialogStateFormManagement({
    dialogState,
    useFormReturn: settingsUseFormReturn,
  });
  useDialogStateFormManagement({
    dialogState,
    useFormReturn: contentUseFormReturn,
  });

  const { dialogContentProps } = useDialogViews<"settings" | "content">(
    {
      settings: {
        size: "small",
        view: (props) => (
          <PostTemplateSettingsDialogView
            analyticsType="create"
            title="Create message template"
            submitButtonText="Continue"
            onSubmit={(submittedSettingsData) => {
              setSettingsData(submittedSettingsData);
              props.setView("content");
            }}
            initialFocusRef={
              props.initialFocusRef as RefObject<HTMLInputElement>
            }
            useFormReturn={settingsUseFormReturn}
            dialogState={dialogState}
          />
        ),
      },
      content: {
        size: "medium",
        backTarget: "settings",
        view: (props) => (
          <PostTemplateContentDialogView
            analyticsType="create"
            title={settingsData?.name ?? "Create message template"}
            submitButtonText="Create"
            submitLoading={upsertPostTemplateLoading}
            onSubmit={(submittedContentData) => {
              if (!settingsData) {
                throw new Error("Settings data must exist");
              }

              upsertPostTemplate({
                variables: {
                  input: {
                    name: settingsData.name,
                    departmentId: settingsData.departmentId,
                    data: submittedContentData.data,
                    title: submittedContentData.title,
                  },
                },
                onCompleted: () => {
                  dialogState.hide();
                },
              });
            }}
            initialFocusRef={
              props.initialFocusRef as RefObject<HTMLInputElement>
            }
            useFormReturn={contentUseFormReturn}
            dialogState={dialogState}
            organization={organization}
          />
        ),
      },
    },
    { initialView: "settings" }
  );

  return (
    <Dialog.Root state={dialogState}>
      <Dialog.Content
        {...dialogContentProps}
        // Disabled due to bug when used with useLeaveConfirmation
        hideOnInteractOutside={false}
      />
    </Dialog.Root>
  );
}
